import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { API_ENDPOINTS, METHOD_TYPE } from "../../utils/apiUrls";
import api from "../../utils/api";

const initialState = {
  isLoading: false,
  error: null,
  planLists: [],
  entityActivePlan: {},
  packageDetails: {},
  packageList: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
  inquiriesCount: {},
  inquiriesList: { rows: [], totalCount: 0, currentPage: 1, totalPages: 1 },
};

const getRows = (payload) => {
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload)) return payload;
  return [];
};

const getCountryRuleConfig = (plan) =>
  plan?.countryRuleConfig ||
  plan?.country_rule_config ||
  plan?.planMeta?.countryRuleConfig ||
  plan?.plan_meta?.countryRuleConfig ||
  plan?.metadata?.countryRuleConfig ||
  {};

const getCountryNameFromConfig = (countryConfig = {}, fallback = "") => {
  const selectedCountry =
    countryConfig?.selectedCountry || countryConfig?.selected_country || {};
  return String(
    selectedCountry?.name ||
      selectedCountry?.countryName ||
      selectedCountry?.country_name ||
      countryConfig?.countryName ||
      countryConfig?.country_name ||
      fallback ||
      "",
  ).trim();
};

const getCountryTaxDisplayName = (countryName = "") => {
  const resolvedCountryName = String(countryName || "").trim();
  return resolvedCountryName
    ? `${resolvedCountryName} Tax`
    : "Selected country tax";
};

const sanitizeCountryTaxBreakdown = (taxBreakdown = [], countryName = "") => {
  if (!Array.isArray(taxBreakdown)) return [];
  return taxBreakdown.map((tax) => ({
    ...tax,
    name:
      tax?.name ||
      tax?.tax_name ||
      tax?.label ||
      getCountryTaxDisplayName(countryName),
    tax_name:
      tax?.tax_name ||
      tax?.name ||
      tax?.label ||
      getCountryTaxDisplayName(countryName),
    label:
      tax?.label ||
      tax?.tax_name ||
      tax?.name ||
      getCountryTaxDisplayName(countryName),
  }));
};

const normalizeSelfServicePlan = (plan) => {
  const countryRuleConfig = getCountryRuleConfig(plan);
  const pricingSnapshot =
    countryRuleConfig?.pricingSnapshot ||
    countryRuleConfig?.pricing_snapshot ||
    {};
  const currency =
    pricingSnapshot?.currency || countryRuleConfig?.local?.currency || "AED";
  const planId = plan?.id || plan?.plan_id;
  const countryName = getCountryNameFromConfig(
    countryRuleConfig,
    pricingSnapshot?.countryName || pricingSnapshot?.country_name,
  );
  const serviceRows = Array.isArray(pricingSnapshot?.serviceFees)
    ? pricingSnapshot.serviceFees
    : Array.isArray(pricingSnapshot?.service_fees)
      ? pricingSnapshot.service_fees
      : [];
  const services = serviceRows.reduce((acc, service) => {
    const key = service?.serviceKey || service?.service_key;
    if (!key) return acc;
    acc[key] = {
      type: service?.category || "",
      is_checked: Number(service?.price || 0) > 0,
      price: service?.price ?? 0,
      service_provider: service?.provider || "",
    };
    return acc;
  }, {});
  const taxBreakdown = Array.isArray(pricingSnapshot?.taxBreakdown)
    ? pricingSnapshot.taxBreakdown
    : Array.isArray(pricingSnapshot?.tax_breakdown)
      ? pricingSnapshot.tax_breakdown
      : [];
  const displayTaxBreakdown = sanitizeCountryTaxBreakdown(
    taxBreakdown,
    countryName,
  );

  return {
    ...plan,
    plan_id: planId,
    package_id: plan?.package_id || planId,
    package_name: plan?.package_name || "Self Service Plan",
    package_type: plan?.package_type || "self_service",
    self_service_plan_id: planId,
    is_self_service_plan: true,
    currency,
    currency_code: currency,
    services: Object.keys(services).length > 0 ? services : plan?.services,
    platform_fee:
      pricingSnapshot?.platformFee ??
      pricingSnapshot?.platform_fee ??
      plan?.platform_fee ??
      0,
    minimum_monthly_price:
      pricingSnapshot?.totalAmount ??
      pricingSnapshot?.total_amount ??
      plan?.minimum_monthly_price ??
      0,
    package_price:
      pricingSnapshot?.totalAmount ??
      pricingSnapshot?.total_amount ??
      plan?.package_price ??
      0,
    base_amount:
      pricingSnapshot?.taxableSubtotal ??
      pricingSnapshot?.taxable_subtotal ??
      0,
    tax_amount: pricingSnapshot?.totalTax ?? pricingSnapshot?.total_tax ?? 0,
    total_tax_rate: displayTaxBreakdown.reduce(
      (sum, tax) => sum + Number(tax?.rate ?? tax?.tax_rate ?? 0),
      0,
    ),
    tax_names: displayTaxBreakdown
      .map((tax) => tax?.name || tax?.tax_name)
      .filter(Boolean)
      .join(", "),
    taxes: displayTaxBreakdown.map((tax) => ({
      tax_name: tax?.name || tax?.tax_name,
      tax_rate: tax?.rate ?? tax?.tax_rate,
      tax_amount: tax?.amount ?? tax?.tax_amount,
    })),
    pricingSnapshot,
    pricing_snapshot: pricingSnapshot,
    countryRuleConfig,
    country_rule_config: countryRuleConfig,
  };
};

export const fetchActivePlanLists = createAsyncThunk(
  "tax/fetchAllTax",
  async (queryParam = "") => {
    try {
      const response = await api({
        method: METHOD_TYPE.get,
        url: API_ENDPOINTS.fetchActivePlan + queryParam,
      });

      const onboardingResponses = await Promise.allSettled([
        api({
          method: METHOD_TYPE.get,
          url: `${API_ENDPOINTS.getOnboardingPlans}?journeyType=individual`,
        }),
        api({
          method: METHOD_TYPE.get,
          url: `${API_ENDPOINTS.getOnboardingPlans}?journeyType=corporate`,
        }),
      ]);

      const packageRows = getRows(response.data);
      const onboardingRows = onboardingResponses
        .filter((result) => result.status === "fulfilled")
        .flatMap((result) => getRows(result.value?.data))
        .map(normalizeSelfServicePlan);
      const seenPlanIds = new Set();
      const uniqueOnboardingRows = onboardingRows.filter((plan) => {
        const planId = String(
          plan?.self_service_plan_id ?? plan?.plan_id ?? plan?.id ?? "",
        );
        if (!planId) return true;
        if (seenPlanIds.has(planId)) return false;
        seenPlanIds.add(planId);
        return true;
      });

      return {
        ...response.data,
        data: [...packageRows, ...uniqueOnboardingRows],
      };
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const sendCustomPackageInquiryEmail = createAsyncThunk(
  "plan/sendCustomPackageInquiryEmail",
  async () => {
    try {
      let data = {
        method: METHOD_TYPE.post,
        url: API_ENDPOINTS.sendCustomPackageInquiryEmail,
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const submitCustomPackageInquiry = createAsyncThunk(
  "plan/submitCustomPackageInquiry",
  async (message) => {
    try {
      let data = {
        method: METHOD_TYPE.post,
        url: API_ENDPOINTS.submitCustomPackageInquiry,
        data: { message },
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const getInquiriesCount = createAsyncThunk(
  "plan/getInquiriesCount",
  async () => {
    try {
      let data = {
        method: METHOD_TYPE.get,
        url: API_ENDPOINTS.getInquiriesCount,
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const getAllInquiries = createAsyncThunk(
  "plan/getAllInquiries",
  async (params) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      let data = {
        method: METHOD_TYPE.get,
        url: API_ENDPOINTS.getAllInquiries + "?" + queryString,
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const updateInquiryStatus = createAsyncThunk(
  "plan/updateInquiryStatus",
  async ({ inquiryId, status }) => {
    try {
      let data = {
        method: METHOD_TYPE.put,
        url: API_ENDPOINTS.updateInquiryStatus,
        data: { inquiryId, status },
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const initiateOrder = createAsyncThunk(
  "order/initiateOrder",
  async (requestObject) => {
    try {
      let data = {
        method: METHOD_TYPE.post,
        url: API_ENDPOINTS.initiateOrder,
        data: requestObject,
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const createPublicOnboardingPlan = createAsyncThunk(
  "plan/createPublicOnboardingPlan",
  async (payload) => {
    try {
      const response = await api({
        method: METHOD_TYPE.post,
        url: API_ENDPOINTS.createPublicOnboardingPlan,
        data: payload,
      });
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const previewPublicOnboardingPlan = createAsyncThunk(
  "plan/previewPublicOnboardingPlan",
  async (payload) => {
    try {
      const response = await api({
        method: METHOD_TYPE.post,
        url: API_ENDPOINTS.previewPublicOnboardingPlan,
        data: payload,
        headers: { Authorization: "" },
        _skipEncryption: true,
        _skipTokenCheck: true,
      });
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const placeOrder = createAsyncThunk(
  "order/placeOrder",
  async (requestObject) => {
    try {
      let data = {
        method: METHOD_TYPE.post,
        url: API_ENDPOINTS.placeOrder,
        data: requestObject,
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const fetchEntityActivePlan = createAsyncThunk(
  "package/getEntityActivePackage",
  async () => {
    try {
      let data = {
        method: METHOD_TYPE.get,
        url: API_ENDPOINTS.getEntityActivePackage,
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const getAllPlanList = createAsyncThunk(
  "package/getAllPackage",
  async (queryParam = "") => {
    try {
      let data = {
        method: METHOD_TYPE.get,
        url: API_ENDPOINTS.getAllPackage + queryParam,
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const deactivatePackage = createAsyncThunk(
  "package/deactivatePackage",
  async (package_id) => {
    try {
      let data = {
        method: METHOD_TYPE.delete,
        url: API_ENDPOINTS.deactivatePackage + "?package_id=" + package_id,
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const updatePackageData = createAsyncThunk(
  "package/updatePackage",
  async (requestBody) => {
    try {
      let data = {
        method: METHOD_TYPE.put,
        url: API_ENDPOINTS.updatePackage,
        data: requestBody,
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const createNewPackage = createAsyncThunk(
  "package/createNewPackage",
  async (requestBody) => {
    try {
      let data = {
        method: METHOD_TYPE.post,
        url: API_ENDPOINTS.createNewPackage,
        data: requestBody,
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const packageUpdate = createAsyncThunk(
  "package/update",
  async (requestBody) => {
    try {
      let data = {
        method: METHOD_TYPE.post,
        url: API_ENDPOINTS.packageUpdate,
        data: requestBody,
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

export const getPackageDetails = createAsyncThunk(
  "package/getPackageDetails",
  async (queryParam = "") => {
    try {
      let data = {
        method: METHOD_TYPE.get,
        url: API_ENDPOINTS.getPackageDetails + queryParam,
      };
      const response = await api(data);
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
);

const planSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    resetPlanList(state, action) {
      state.packageList = {
        rows: [],
        count: 0,
        totalPages: 0,
        currentPage: 1,
        type: 200,
      };
    },
    resetPlanDetails(state, action) {
      state.packageDetails = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivePlanLists.pending, (state) => {
        state.isLoading = true;
        state.planLists = [];
      })
      .addCase(fetchActivePlanLists.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.planLists = getRows(payload);
      })
      .addCase(
        sendCustomPackageInquiryEmail.fulfilled,
        (state, { payload }) => {
          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(submitCustomPackageInquiry.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getInquiriesCount.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.inquiriesCount = payload.data;
      })
      .addCase(getAllInquiries.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.inquiriesList = payload.data;
      })
      .addCase(updateInquiryStatus.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(initiateOrder.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createPublicOnboardingPlan.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(placeOrder.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchEntityActivePlan.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.entityActivePlan = payload.data;
      })
      .addCase(getAllPlanList.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.packageList = payload.data;
      })
      .addCase(deactivatePackage.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updatePackageData.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createNewPackage.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(packageUpdate.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getPackageDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.packageDetails = payload.data;
      })
      .addMatcher(
        isAnyOf(
          sendCustomPackageInquiryEmail.pending,
          submitCustomPackageInquiry.pending,
          getInquiriesCount.pending,
          getAllInquiries.pending,
          initiateOrder.pending,
          createPublicOnboardingPlan.pending,
          placeOrder.pending,
          fetchEntityActivePlan.pending,
          getAllPlanList.pending,
          deactivatePackage.pending,
          updatePackageData.pending,
          createNewPackage.pending,
          packageUpdate.pending,
          getPackageDetails.pending,
        ),
        (state) => {
          state.isLoading = true;
        },
      )
      .addMatcher(
        isAnyOf(
          fetchActivePlanLists.rejected,
          sendCustomPackageInquiryEmail.rejected,
          submitCustomPackageInquiry.rejected,
          getInquiriesCount.rejected,
          getAllInquiries.rejected,
          initiateOrder.rejected,
          createPublicOnboardingPlan.rejected,
          placeOrder.rejected,
          fetchEntityActivePlan.rejected,
          getAllPlanList.rejected,
          deactivatePackage.rejected,
          updatePackageData.rejected,
          createNewPackage.rejected,
          packageUpdate.rejected,
          getPackageDetails.rejected,
        ),
        (state, { error }) => {
          state.isLoading = false;
          state.error = error.message
            ? error.message
            : "Request Failed Please Try Again ";
          if (error.type === "fetchActivePlanLists/rejected") {
            state.planLists = [];
          }
        },
      );
  },
});

const { resetPlanList, resetPlanDetails } = planSlice.actions;
const planSliceReducer = planSlice.reducer;

export { resetPlanList, resetPlanDetails, planSliceReducer };
export default planSliceReducer;
